import type { AutomationAppUIModel } from '@/types/automation';
import { isLocalMode, getLocalMockApps } from './localRuntimeClient';
import { getArmBaseUrl } from './armConfig';

const RESOURCE_GRAPH_URL = `${getArmBaseUrl()}/providers/Microsoft.ResourceGraph/resources?api-version=2021-03-01`;

const OTTO_APPS_QUERY = `
Resources
| where type == "microsoft.app/containerapps"
| where tags contains "otto"
| extend fqdn = tostring(properties.configuration.ingress.fqdn)
| project id, name, location, resourceGroup, subscriptionId, fqdn
`;

type ResourceGraphApiResponse = {
  totalRecords: number;
  count: number;
  data: Array<{
    id: string;
    name: string;
    location: string;
    resourceGroup: string;
    subscriptionId: string;
    fqdn?: string;
  }>;
};

/**
 * Fetches the FQDN for a single ACA app via ARM API.
 * Used as fallback when Resource Graph doesn't return the fqdn.
 */
const fetchAcaFqdn = async (accessToken: string, resourceId: string): Promise<string | undefined> => {
  try {
    const url = `${getArmBaseUrl()}${resourceId}?api-version=2024-03-01`;
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) return undefined;
    const data = await response.json();
    return data?.properties?.configuration?.ingress?.fqdn;
  } catch {
    return undefined;
  }
};

/**
 * Fetches Otto-tagged ACA workflow apps.
 * In local mode, returns a mock entry pointing to the local runtime.
 * In cloud mode, queries Azure Resource Graph for Container Apps with the "otto" tag.
 * @param subscriptionIds - Optional subscription IDs to scope the query
 */
export const fetchOttoApps = async (accessToken: string, subscriptionIds?: string[]): Promise<AutomationAppUIModel[]> => {
  // In local mode, return mock local apps for all seeded projects
  if (isLocalMode()) {
    const mockProjects = ['local-project', 'staging-project', 'production-project'];
    return mockProjects.flatMap((project) => getLocalMockApps(project));
  }

  console.log('[resourceGraph] Fetching otto ACA apps from Resource Graph...');

  const response = await fetch(RESOURCE_GRAPH_URL, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: OTTO_APPS_QUERY,
      ...(subscriptionIds?.length ? { subscriptions: subscriptionIds } : {}),
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[resourceGraph] Error response:', errorText);
    throw new Error(`Failed to fetch apps: ${response.status} ${errorText}`);
  }

  const result: ResourceGraphApiResponse = await response.json();
  console.log('[resourceGraph] Found', result.data.length, 'apps:', result.data.map(a => ({ name: a.name, fqdn: a.fqdn })));

  // Map results, fetching FQDN from ARM as fallback if Resource Graph didn't return it
  const apps = await Promise.all(
    result.data.map(async (item) => {
      let fqdn = item.fqdn;
      if (!fqdn) {
        console.warn(`[resourceGraph] No FQDN from Resource Graph for ${item.name}, fetching from ARM...`);
        fqdn = await fetchAcaFqdn(accessToken, item.id);
      }
      return {
        name: item.name,
        type: 'applications' as const,
        projectName: item.resourceGroup,
        armResourceId: item.id,
        location: item.location,
        ...(fqdn ? { fqdn } : {}),
        properties: {
          applicationId: item.name,
          appResourceId: item.id,
          createdBy: '',
          createdOn: '',
          updatedBy: '',
          updatedOn: '',
        },
      };
    })
  );

  return apps;
};

// ─── Automation projects ──────────────────────────────────────────────────────

const OTTO_PROJECTS_QUERY = `
Resources
| where type == "microsoft.logic/automationprojects"
| project id, name, location, resourceGroup, subscriptionId
`;

type ProjectGraphRow = {
  id: string;
  name: string;
  location: string;
  resourceGroup: string;
  subscriptionId: string;
};

export interface OttoProjectArmRef {
  /** Full ARM resource id, e.g. `/subscriptions/.../automationProjects/foo`. */
  armId: string;
  /** ARM resource name (matches the user-facing project name in Auto). */
  name: string;
  /** Azure region the resource lives in, lowercased by Resource Graph. */
  location: string;
}

/**
 * Resolve the full ARM resource ids for every `Microsoft.Logic/automationProjects`
 * resource visible to the caller. Otto's data-plane returns short, internal ids
 * (`/automationProjects/<guid>`) which can't be used to deep-link into the Azure
 * portal — Resource Graph is the only source for the canonical ARM id.
 *
 * @param accessToken - An ARM-scoped access token (`getArmToken()`).
 * @param subscriptionIds - Optional subscription scope; omit to query all
 *   subscriptions accessible to the caller.
 */
export const fetchOttoProjectArmRefs = async (
  accessToken: string,
  subscriptionIds?: string[],
): Promise<OttoProjectArmRef[]> => {
  if (isLocalMode()) return [];

  const response = await fetch(RESOURCE_GRAPH_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: OTTO_PROJECTS_QUERY,
      ...(subscriptionIds?.length ? { subscriptions: subscriptionIds } : {}),
    }),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Failed to fetch automation projects: ${response.status} ${errorText}`);
  }

  const result: { data: ProjectGraphRow[] } = await response.json();
  return result.data.map((row) => ({
    armId: row.id,
    name: row.name,
    location: row.location,
  }));
};
